import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { createStore, useStore } from 'zustand'
import type { StoreApi } from 'zustand'
import { useUtilsStore } from './UtilsContext'
import { useAdaptiveCardStore } from './AdaptiveCardContext'
import { useSpeechStore } from './SpeechContext'
import { useRetorikStore } from './RetorikContext'
import { useLocaleStore } from './LocaleContext'

import type { Action, SuggestedActionAttachment } from '../../models/suggestedActionAttachment'
import { RetorikActivity } from '../../models/activityTypes'
import { CONTENT_TYPE_INTERNAL_COUNTER, CONTENT_TYPE_LINK, CONTENT_TYPE_NAMED_LIST, CONTENT_TYPE_SUGGESTEDACTION, CONTENT_TYPE_SUGGESTION } from '../../models/attachmentTypes'
import { nonResponseInducingEvents } from '../../models/constants'
import { InputHintBehaviour, Mode } from '../../models/enums'
import { processMarkdownAndSpeechMarkdown } from '../../utils/markdownUtils'
import { processAttachments } from '../../utils/processAttachments'
import createLinkAttachmentCard, { createAttachmentsFromMultiLink } from '../../utils/createLinkAttachmentCard'
import createImageAttachmentCard from '../../utils/createImageAttachmentCard'
import processDirectLineIncomingActivity from '../../utils/processDirectlineIncomingActivity'
import { DirectLineAttachment } from '../../models/directLine'

interface ActivityState {
  // All activities
  activities: Array<RetorikActivity>
  endedActivities: Array<string>
  // (all / last) message / events activities
  lastActivity: RetorikActivity | undefined
  messageActivities: Array<RetorikActivity>
  lastMessageActivity: RetorikActivity | undefined
  eventActivities: Array<RetorikActivity>
  lastEventActivity: RetorikActivity | undefined
  // Bot activities
  lastBotActivity: RetorikActivity | undefined
  botMessageActivities: Array<RetorikActivity>
  lastBotMessageActivity: RetorikActivity | undefined
  botEventActivities: Array<RetorikActivity>
  lastBotEventActivity: RetorikActivity | undefined
  botStreamingActivities: Map<string, Array<RetorikActivity>> // Map with question id -> array of streaming answers, used for text display
  // User activities
  userMessageActivities: Array<RetorikActivity>
  lastUserMessageActivity: RetorikActivity | undefined
  // Miscellaneous
  watermark: number
  conversationId: string | null
  retrievingConversation: boolean
  addRetorikNewsConversationIdInLocalStorage: string | undefined
  // Cookie utils
  cookies: Array<string>
  // Queue
  queue: Array<RetorikActivity>
  queueLock: boolean
  actions: {
    addCookie: (value: string | null) => void
    setCookiesInBrowser: () => void
    checkCurrentConversationId: (value: string | undefined) => boolean
    checkConversationId: (value: string) => void
    processQueue: () => Promise<void>
    processAddBotMessage: (value: RetorikActivity) => Promise<void>
    addBotMessage: (value: RetorikActivity) => Promise<void>
    processAddBotEvent: (value: RetorikActivity) => Promise<void>
    addBotEvent: (value: RetorikActivity) => Promise<void>
    addUserMessage: (value: RetorikActivity) => Promise<void>
    addUserEvent: (value: RetorikActivity) => void
    addEndedActivity: (value: string) => void
    setRetrievingConversation: (value: boolean) => void
    setAddRetorikNewsConversationIdInLocalStorage: (value: string | undefined) => void
    resetActivityStore: () => void
    processBotMessageActivity: (activity: RetorikActivity) => Promise<RetorikActivity>
  }
}

const initialState = {
  activities: [],
  endedActivities: [],
  lastActivity: undefined,
  lastBotActivity: undefined,
  messageActivities: [],
  lastMessageActivity: undefined,
  eventActivities: [],
  lastEventActivity: undefined,
  botMessageActivities: [],
  lastBotMessageActivity: undefined,
  botEventActivities: [],
  lastBotEventActivity: undefined,
  botStreamingActivities: new Map(),
  userMessageActivities: [],
  lastUserMessageActivity: undefined,
  conversationId: null,
  watermark: 0,
  retrievingConversation: false,
  addRetorikNewsConversationIdInLocalStorage: undefined,
  cookies: [],
  queue: [],
  queueLock: false
}

const regexDataCommand = /(\{data-command=")[\p{L}\p{N}\p{Po}\p{Pd}\p{Pc}\s\u0027\u2019]+("\})(<br\s\/>)?/gu

const createActivityStore = (
  localeRef: React.RefObject<string>,
  isUsedOnBorneRef: React.RefObject<boolean | undefined>,
  inputHintBehaviourRef: React.RefObject<InputHintBehaviour | undefined>,
  setCurrentId: (value: string | undefined) => void,
  setRestoreInputsState: (value: boolean) => void,
  addToStreamingQueue: (value: RetorikActivity) => void,
  setMode: (value: Mode) => void,
  unlockClickedButton: () => void,
  setSubmitDelayBeforeSendingMessage: (value: number | undefined) => void,
  setIsWaitingForResponse: (value: boolean) => void,
  setSendBoxPlaceHolder: (value: string | undefined) => void
) => {
  return createStore<ActivityState>((set, get) => {
    return {
      ...initialState,
      actions: {
        addCookie: (value: string | null): void => {
          value && set({ cookies: [...get().cookies, value] })
        },
        setCookiesInBrowser: (): void => {
          const cookies = get().cookies
          if (cookies.length) {
            cookies.forEach((cookie) => {
              if (cookie.indexOf('<conversationId>') > -1) {
                const conversationId = get().conversationId

                if (conversationId) {
                  const processedCookie = cookie.replace('<conversationId>', conversationId)
                  document.cookie = processedCookie
                }
              } else {
                document.cookie = cookie
              }
            })

            set({ cookies: [] })
          }
        },
        checkCurrentConversationId: (value: string | undefined): boolean => {
          const currentConversationId = get().conversationId
          return !!(currentConversationId && value?.includes(currentConversationId))
        },
        checkConversationId: (value: string): void => {
          // Add current conversation id with tenant name in local storage if we are in a RetorikNews component.
          // This prevents creating a new conversation each time RetorikNews is launched (used in Retorik Kiosk).
          if (get().addRetorikNewsConversationIdInLocalStorage) {
            const dataToPutInlocalStorage = {
              conversationId: value,
              startedAt: Date.now()
            }

            localStorage.setItem(`Retorik.News.Conversation.${get().addRetorikNewsConversationIdInLocalStorage}`, JSON.stringify(dataToPutInlocalStorage))

            set({
              addRetorikNewsConversationIdInLocalStorage: undefined
            })
          }

          // Check directline id to know if we have to reset the activities
          if (value && value !== get().conversationId) {
            get().conversationId === null ? set({ conversationId: value }) : set({ ...initialState, conversationId: value })

            // Set desired cookies once the conversation id is set / changed
            get().actions.setCookiesInBrowser()
          }
        },
        processQueue: async (): Promise<void> => {
          if (get().queue.length) {
            const currentActivity = get().queue[0]
            currentActivity.type === 'message' ? await get().actions.processAddBotMessage(currentActivity) : await get().actions.processAddBotEvent(currentActivity)
            // Remove the first element from the queue (the one that got processed above) and process the queue once again
            set({ queue: get().queue.slice(1) })
            get().actions.processQueue()
          } else {
            set({ queueLock: false })
          }
        },
        processAddBotMessage: async (value: RetorikActivity): Promise<void> => {
          setCurrentId(undefined)
          const processedActivity = await get().actions.processBotMessageActivity(value)
          const tempActivity = get().retrievingConversation ? undefined : processedActivity
          // Add this activity to the streaming queue if we are not in conversation retrieval process
          !get().retrievingConversation && addToStreamingQueue(processedActivity)

          set({
            activities: [...get().activities, processedActivity],
            lastActivity: tempActivity,
            lastBotActivity: tempActivity,
            messageActivities: [...get().messageActivities, processedActivity],
            botMessageActivities: [...get().botMessageActivities, processedActivity],
            lastMessageActivity: tempActivity,
            lastBotMessageActivity: tempActivity,
            watermark: get().watermark + 1
          })

          Promise.resolve()
        },
        addBotMessage: async (value: RetorikActivity): Promise<void> => {
          if (get().actions.checkCurrentConversationId(value.id)) {
            if (get().queueLock) {
              set({ queue: [...get().queue, value] })
            } else {
              set({ queueLock: true })
              await get().actions.processAddBotMessage(value)
              unlockClickedButton()
              setIsWaitingForResponse(false)
              get().actions.processQueue()
            }
          }
        },
        processAddBotEvent: async (value: RetorikActivity): Promise<void> => {
          const streamingMap = new Map(get().botStreamingActivities)

          // Deal with streaming activities
          if (value.name?.toLowerCase() === 'davi.streammessageactivity') {
            // Add this activity to the streaming queue if we are not in conversation retrieval process
            if (value.text?.trim().length && !get().retrievingConversation) {
              // Create speak field from the text one
              const processedText = processMarkdownAndSpeechMarkdown(value.text, true)
              value.speak = processedText.text
              value.htmlText = processedText.htmlText
              addToStreamingQueue(value)
              // Set data in the Map for text display
              if (value.replyToId) {
                const currentStreamingData = streamingMap.get(value.replyToId)
                currentStreamingData ? streamingMap.set(value.replyToId, [...currentStreamingData, value]) : streamingMap.set(value.replyToId, [value])
              }
            }
          }

          set({
            activities: [...get().activities, value],
            lastActivity: get().retrievingConversation ? undefined : value,
            lastBotActivity: get().retrievingConversation ? undefined : value,
            eventActivities: [...get().eventActivities, value],
            botEventActivities: [...get().botEventActivities, value],
            lastBotEventActivity: value,
            lastEventActivity: value,
            botStreamingActivities: streamingMap,
            watermark: get().watermark + 1
          })

          Promise.resolve()
        },
        addBotEvent: async (value: RetorikActivity): Promise<void> => {
          if (get().actions.checkCurrentConversationId(value.id)) {
            //
            if (value.name?.toLowerCase() === 'switchtotextmode') {
              setMode(Mode.text)
              set({
                watermark: get().watermark + 1
              })
            } else {
              if (get().queueLock) {
                set({ queue: [...get().queue, value] })
              } else {
                set({ queueLock: true })
                await get().actions.processAddBotEvent(value)
                unlockClickedButton()
                setIsWaitingForResponse(false)
                get().actions.processQueue()
              }
            }
          }
        },
        addUserMessage: async (value: RetorikActivity): Promise<void> => {
          if (get().actions.checkCurrentConversationId(value.id)) {
            setIsWaitingForResponse(true)

            set({
              activities: [...get().activities, value],
              lastActivity: value,
              messageActivities: [...get().messageActivities, value],
              userMessageActivities: [...get().userMessageActivities, value],
              lastMessageActivity: value,
              lastUserMessageActivity: value,
              watermark: get().watermark + 1
            })
          }
        },
        addUserEvent: (value: RetorikActivity): void => {
          if (get().actions.checkCurrentConversationId(value.id)) {
            // Only wait for response if the event is one that need it
            !nonResponseInducingEvents.find((eventName) => value.name?.toLowerCase().includes(eventName)) && setIsWaitingForResponse(true)

            set({
              activities: [...get().activities, value],
              lastActivity: value,
              lastEventActivity: value,
              eventActivities: [...get().eventActivities, value],
              watermark: get().watermark + 1
            })
          }
        },
        addEndedActivity: (value: string): void => {
          if (!get().endedActivities.includes(value)) {
            set({ endedActivities: [...get().endedActivities, value] })
          }
        },
        setRetrievingConversation: (value: boolean): void => {
          set({ retrievingConversation: value })
        },
        setAddRetorikNewsConversationIdInLocalStorage: (value: string | undefined): void => {
          set({
            addRetorikNewsConversationIdInLocalStorage: value
          })
        },
        resetActivityStore: (): void => {
          set({ ...initialState })
        },
        processBotMessageActivity: async (activity: RetorikActivity): Promise<RetorikActivity> => {
          setRestoreInputsState(!!activity?.channelData?.restoreInputsState)
          setSendBoxPlaceHolder(activity.value?.retorik?.placeHolder || undefined)
          setSubmitDelayBeforeSendingMessage(activity.value?.retorik?.submitDelay)

          if (activity.attachmentLayout?.toLowerCase() === 'namedlist' && activity.attachments?.length) {
            const content = activity.attachments[0].content?.value

            if (Array.isArray(content) && content.length > 12) {
              const title = activity.attachments[0].content?.title
              const lastContent = content.pop()
              const nbAttachments = Math.ceil(content.length / 11)
              const attachments: Array<DirectLineAttachment> = []
              const locale = localeRef.current || 'fr-FR'

              // Set 'group' data (initial of the lastname) for each item
              content.forEach((item) => {
                if (item.text) {
                  const splitName = (item.text as string).split(' ')
                  if (splitName.length > 1) {
                    item.group = splitName[1].charAt(0).toLocaleUpperCase(locale)
                  } else {
                    item.group = splitName[0].charAt(0).toLocaleUpperCase(locale)
                  }
                }
              })

              for (let i = 0; i < nbAttachments; i++) {
                attachments.push({
                  contentType: CONTENT_TYPE_NAMED_LIST,
                  content: {
                    title: title,
                    value: [...content.slice(i * 11, i * 11 + 11), lastContent]
                  }
                })
              }

              activity.attachments = attachments
            }
          }

          // If the application is used on a borne and there are attachments, check if a received attachment is a multi-link one.
          // If there is some, we need to split it in several attachments, one for each link.
          if (isUsedOnBorneRef.current && activity.attachments?.length) {
            let count = 0
            const tempAttachments: Array<any> = []
            for (const attachment of activity.attachments) {
              if (attachment.contentType === CONTENT_TYPE_LINK && Array.isArray(attachment.content?.urlData)) {
                count++
                const splitAttachments = await createAttachmentsFromMultiLink(attachment.content.urlData)
                tempAttachments.push(...splitAttachments)
              } else {
                tempAttachments.push(attachment)
              }
            }
            // If some attachments have been split, we replace the attachments in the activity
            count && (activity.attachments = tempAttachments)
            // Carousel display if there is more than 1 card
            count && activity.attachments.length > 1 && (activity.attachmentLayout = 'carousel')
          }

          const processedData = processDirectLineIncomingActivity(activity)
          // Set processed speak, text and attachments if there are some
          processedData.speak && (activity.speak = processedData.speak)
          processedData.text && (activity.text = processedData.text)
          processedData.htmlText && (activity.htmlText = processedData.htmlText)

          // Check if there are cards to create from images
          if (processedData.images.length) {
            // If there is no attachment field, create an empty one
            activity.attachments === undefined && (activity.attachments = [])
            // Create content from image data and add it to the attachments
            for (const image of processedData.images) {
              const imageAttachment = await createImageAttachmentCard(image)
              activity.attachments.push(imageAttachment)
            }
            activity.attachments.length > 1 && (activity.attachmentLayout = 'carousel')
          }

          // Check if there are cards to create from links
          if (processedData.urls.length) {
            // If there is no attachment field, create an empty one
            activity.attachments === undefined && (activity.attachments = [])
            // Create content asynchonously from url data and add it to the attachments
            for (const url of processedData.urls) {
              const fetchedAttachment = await createLinkAttachmentCard(url)
              activity.attachments.push(fetchedAttachment)
            }
            activity.attachments.length > 1 && (activity.attachmentLayout = 'carousel')
          }

          // Transform herocards in adaptivecards
          const heroToAdaptiveAttachments = processAttachments(activity.attachments)
          heroToAdaptiveAttachments && (activity.attachments = [...heroToAdaptiveAttachments])

          // Transform suggested actions into cards if the display isn't daviList
          if (activity.suggestedActions?.actions && activity.attachmentLayout?.toLowerCase() !== 'davilist') {
            let actionsArray: Array<Action> = []
            activity.suggestedActions.actions.forEach((suggestedAction) => {
              const action: Action = {
                title: suggestedAction.title,
                action: suggestedAction.value
              }

              actionsArray = [...actionsArray, action]
            })

            const suggestedActionAttachment: SuggestedActionAttachment = {
              contentType: CONTENT_TYPE_SUGGESTEDACTION,
              content: {
                title: activity.text || '',
                actions: actionsArray
              }
            }

            // Create field attachments with the newly created one if no attachment yet, or add to existing ones
            activity.attachments === undefined ? (activity.attachments = [suggestedActionAttachment]) : activity.attachments.push(suggestedActionAttachment)
          }

          // Deal with text containing [data command] data to make them usable in text mode
          if (activity?.text && activity.text.includes('{data-command=')) {
            const splitText: Array<string> = activity.text.split('{data-command=')
            const dataForAttachment: Array<{
              text: string
              differentTextToSend: string
            }> = []
            for (let i = 1; i < splitText.length; i++) {
              const text = splitText[i - 1].substring(splitText[i - 1].lastIndexOf('\n') ? splitText[i - 1].lastIndexOf('\n') + 1 : 0).trim()
              const replacement = splitText[i].substring(0, splitText[i].indexOf('}')).replaceAll('"', '')

              dataForAttachment.push({
                text: text,
                differentTextToSend: replacement
              })

              // Remove text part from speak / text / htmlText
              activity.speak && (activity.speak = activity.speak.replace(text, ''))
              activity.text = activity.text.replace(text, '')
              activity.htmlText = activity.htmlText?.replace(text, '')
            }

            if (dataForAttachment.length > 0) {
              const suggestionAttachment = {
                contentType: CONTENT_TYPE_SUGGESTION,
                content: {
                  suggestions: dataForAttachment,
                  showTutorial: false
                }
              }

              // Create field attachment swith the newly created one if no attachment yet, or add to existing ones
              activity.attachments === undefined ? (activity.attachments = [suggestionAttachment]) : activity.attachments.push(suggestionAttachment)
            }

            // Remove all [data command] from speak / text / htmlText
            activity.speak && (activity.speak = activity.speak.replaceAll(regexDataCommand, ''))
            activity.text = activity.text.replaceAll(regexDataCommand, '')
            activity.htmlText = activity.htmlText?.replaceAll(regexDataCommand, '')
          }

          if ((activity.value?.retorik?.placeHolder || activity.value?.retorik?.submitDelay) && !activity.attachments?.length) {
            activity.attachments = [
              {
                contentType: CONTENT_TYPE_INTERNAL_COUNTER
              }
            ]
          }

          // Process activity inputHint parameter if necessary
          if (inputHintBehaviourRef.current) {
            switch (inputHintBehaviourRef.current.toLowerCase()) {
              case InputHintBehaviour.always:
                // Force value to 'expectingInput' to trigger microphone opening in vocal mode
                activity.inputHint = 'expectingInput'
                break
              case InputHintBehaviour.ignore:
              case InputHintBehaviour.never:
                // Force value to 'ignoreInput' to prevent microphone opening in vocal mode
                activity.inputHint = 'ignoreInput'
                break
              case InputHintBehaviour.auto:
              default:
                // No change, activity's current value will be used
                break
            }
          }

          return activity
        }
      }
    }
  })
}

const ActivityContext = createContext<StoreApi<ActivityState> | null>(null)

const useActivityStore = <T,>(selector: (state: ActivityState) => T): T => {
  const store = useContext(ActivityContext)
  if (!store) throw new Error('Retorik Framework > useActivityStore must be used within ActivityProvider')
  return useStore(store, selector)
}

const ActivityProvider = ({ children }) => {
  const locale = useLocaleStore((state) => state.locale)
  const configuration = useRetorikStore((state) => state.configuration)

  const localeRef = useRef<string>('fr-FR')
  const isUsedOnBorneRef = useRef<boolean | undefined>(false)
  const inputHintBehaviourRef = useRef<InputHintBehaviour | undefined>(undefined)

  const { setCurrentId, setRestoreInputsState } = useAdaptiveCardStore((state) => state.actions)
  const { addToStreamingQueue, setMode } = useSpeechStore((state) => state.actions)
  const unlockClickedButton = useUtilsStore((state) => state.actions.unlockClickedButton)
  const { setSubmitDelayBeforeSendingMessage, setIsWaitingForResponse, setSendBoxPlaceHolder } = useRetorikStore((state) => state.actions)

  const [store] = useState(() =>
    createActivityStore(
      localeRef,
      isUsedOnBorneRef,
      inputHintBehaviourRef,
      setCurrentId,
      setRestoreInputsState,
      addToStreamingQueue,
      setMode,
      unlockClickedButton,
      setSubmitDelayBeforeSendingMessage,
      setIsWaitingForResponse,
      setSendBoxPlaceHolder
    )
  )

  useEffect(() => {
    localeRef.current = locale
  }, [locale])

  useEffect(() => {
    isUsedOnBorneRef.current = configuration.isUsedOnBorne
  }, [configuration.isUsedOnBorne])

  useEffect(() => {
    inputHintBehaviourRef.current = configuration.speechRecognitionOptions?.inputHintBehaviour
  }, [configuration.speechRecognitionOptions?.inputHintBehaviour])

  return <ActivityContext.Provider value={store}>{children}</ActivityContext.Provider>
}

export default ActivityProvider
export { useActivityStore }
